import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Chatbot from "./components/chatbot/chatbot.jsx";
import ChatPage from "./components/ChatPage/LandingPage.jsx";
import AdminDashboard from "./components/AdminDashboard";
import BrandTerritoryDashboard from "./components/BrandDashboard/BrandTerritoryDashboard";
import SalesDashboard from "./components/SalesDashboard/SalesDashboard";
import AISalesDiagnosisDashboard from "./components/AiSalesDiagnosis/Dashboard.jsx";
import AISalesDiagnosisEmployeeDetail from "./components/AiSalesDiagnosis/EmployeeDetail.jsx";
import SemanticWorkspace from "./components/Semantic/SemanticWorkspace.jsx";
import FridayDashboardLayout from "./components/FridayDashboard/FridayDashboardLayout.jsx";
import FridayDashboardHome from "./components/FridayDashboard/FridayDashboardHome.jsx";
import DepartmentHome from "./components/Departments/DepartmentHome.jsx";
import PharmaDashboard from "./components/PharmaDashboard/App.jsx";
import SfeDashboard from "./components/SfeDashboard/SfeDashboard.jsx";
import EffortsDashboard from "./components/EffortsDashboard/EffortsDashboard.jsx";
import FieldSalesDashboard from "./components/FieldSalesDashboard/FieldSalesDashboard.jsx";
import ExecutiveFocusNotePanel from "./components/ExecutiveFocusNote/ExecutiveFocusNotePanel.jsx";
import { FilterProvider as PharmaDashboardFilterProvider } from "./components/PharmaDashboard/context/FilterContext.jsx";
import { DashboardProvider as AISalesDiagnosisProvider } from "./components/AiSalesDiagnosis/DashboardContext.jsx";
import Login from "./components/login/login.jsx";
import ProtectedRoute from "./components/protectedRoute.jsx";
import MSALProviderWrapper from "./MSALProviderWrapper.jsx";
import { useTokenRefresh } from "./hooks/useTokenRefresh";

/**
 * Single TanStack Query client for the app (used by the Efforts and Field Sales
 * dashboards). Created once at module load so it survives re-renders.
 * - retry: 1 → one retry for transient failures; axiosConfig already handles
 *   401 refresh and 429 back-off, so more retries would only repeat errors.
 * - refetchOnWindowFocus: false → avoid re-running Databricks queries every
 *   time the tab regains focus.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** Restores vertical scrolling: public/style.css sets body { overflow: hidden; height: 100vh }. */
function AISalesDiagnosisScrollLayout() {
  useEffect(() => {
    document.body.classList.add("emcure-dashboard-route-scroll");
    return () => document.body.classList.remove("emcure-dashboard-route-scroll");
  }, []);
  return <Outlet />;
}

function AppContent() {
  useTokenRefresh();

  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route
        path="/departments"
        element={
          <ProtectedRoute>
            <DepartmentHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chatbot"
        element={
          <ProtectedRoute>
            <Chatbot />
            </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdminAccess>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requireSupervisorAccess>
            <AISalesDiagnosisProvider>
              <FridayDashboardLayout />
            </AISalesDiagnosisProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<FridayDashboardHome />} />
        <Route path="sales" element={<SalesDashboard />} />
        <Route path="brand" element={<BrandTerritoryDashboard />} />
        <Route path="diagnosis" element={<AISalesDiagnosisDashboard />} />
        <Route path="diagnosis/employee/:id" element={<AISalesDiagnosisEmployeeDetail />} />
        <Route path="division-performance" element={<PharmaDashboard />} />
        <Route path="sfe" element={<SfeDashboard />} />
        <Route path="efforts" element={<EffortsDashboard />} />
        <Route path="field-sales" element={<FieldSalesDashboard />} />
        <Route
          path="executive-focus-note"
          element={
            <PharmaDashboardFilterProvider>
              <ExecutiveFocusNotePanel />
            </PharmaDashboardFilterProvider>
          }
        />
        <Route
          path="semantic-query"
          element={
            <ProtectedRoute requireSemanticAdminAccess>
              <SemanticWorkspace initialTab="workspace" />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path="/brand-dashboard"
        element={
          <ProtectedRoute requireSupervisorAccess>
            <BrandTerritoryDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-dashboard"
        element={
          <ProtectedRoute requireSupervisorAccess>
            <SalesDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-sales-diagnosis"
        element={
          <ProtectedRoute requireSupervisorAccess>
            <AISalesDiagnosisProvider>
              <AISalesDiagnosisScrollLayout />
            </AISalesDiagnosisProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<AISalesDiagnosisDashboard />} />
        <Route path="employee/:id" element={<AISalesDiagnosisEmployeeDetail />} />
      </Route>

      <Route
        path="/semantic-query-engine"
        element={
          <ProtectedRoute requireSemanticAdminAccess>
            <SemanticWorkspace initialTab="workspace" />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/departments" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <MSALProviderWrapper>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppContent />
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
        </BrowserRouter>
      </QueryClientProvider>
    </MSALProviderWrapper>
  );
}
